import {resolveViewerSmetaBounded} from './viewer-smeta-core';
import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export const queryKeys = {
  models: ['models'] as const,
  hardwarePurchase: (id: string) => ['hardware-purchase', id] as const,
  bundle: (id: string) => ['bundle', id] as const,
  scenePipelineReport: (id: string) => ['scene-pipeline-report', id] as const,
  analysis: (id: string) => ['analysis', id] as const,
  validation: (id: string) => ['validation', id] as const,
  productionReadiness: (id: string) => ['production-readiness', id] as const,
  estimate: (id: string) => ['estimate', id] as const,
  viewerSmetaStatus: (id: string) => ['viewer-smeta-status', id] as const,
  viewerSmetaFlyout: (id: string) => ['viewer-smeta-flyout', id] as const,
  cutlist: (id: string) => ['cutlist', id] as const,
  cutlistNesting: (id: string) => ['cutlist-nesting', id] as const,
  cutlistOffcuts: (id: string) => ['cutlist-offcuts', id] as const,
  history: (id: string) => ['history', id] as const,
  revisionComparison: (id: string, before: string, after: string, historyStamp: number) => ['revision-comparison', id, before, after, historyStamp] as const,
  moduleOperations: (id: string, moduleId: string) => ['module-operations', id, moduleId] as const,
  moduleFill: (id: string, moduleId: string) => ['module-fill', id, moduleId] as const,
  facadeLeafWidthsPreview: (
    id: string,
    moduleId: string,
    payload: Omit<import('./types').FacadeLeafWidthsPreviewPayload, 'expectedBundleVersion'>,
  ) => ['facade-leaf-widths-preview', id, moduleId, payload] as const,
  moduleAtelier: (id: string, moduleId: string) => ['module-atelier', id, moduleId] as const,
  moduleLibrary: (
    preview: 'material' | 'line',
    q = '',
    category = '',
    offset = 0,
    limit = 60,
  ) => ['module-library', preview, q, category, offset, limit] as const,
  materials: ['materials'] as const,
  hardwareCatalog: ['hardware-catalog'] as const,
  moduleGraph: (id: string) => ['module-graph', id] as const,
  bundleGraphs: (id: string) => ['bundle-graphs', id] as const,
  autoDimensions: (id: string) => ['auto-dimensions', id] as const,
  ergonomics: (id: string, statureMm?: number) => ['ergonomics', id, statureMm ?? null] as const,
  productionSpec: (id: string) => ['production-spec', id] as const,
  constraints: (id: string) => ['constraints', id] as const,
  dimensionChain: (id: string, axis: string, moduleIds: string[]) => ['dimension-chain', id, axis, ...moduleIds] as const,
  geometryDiff: (id: string, against: string) => ['geometry-diff', id, against] as const,
  cabinetTemplates: ['cabinet-templates'] as const,
};

export function useCabinetTemplates() {
  return useQuery({
    queryKey: queryKeys.cabinetTemplates,
    queryFn: api.cabinetTemplates,
    staleTime: 30 * 60 * 1000, // presets are static
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// (D12 fix) Default models query now hits the paginated endpoint with
// pageSize=100.  Existing consumers see `data: ModelSummary[]` (we
// unwrap `.models`), so no caller changes needed.  Larger catalogs
// stop shipping all 400+ entries up-front; the search field in TopBar
// triggers a separate `useModelsPaged` for refined queries.
const DEFAULT_MODELS_PAGE_SIZE = 250;

export function useModels() {
  return useQuery({
    queryKey: queryKeys.models,
    queryFn: ({ signal }) => api.modelsPaged({ pageSize: DEFAULT_MODELS_PAGE_SIZE, page: 1 }, signal).then((r) => r.models),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * (D12 fix) Paginated models query — opt-in.  Use this instead of
 * `useModels()` when the catalog grows past a few hundred entries
 * and dropping all of them into the dropdown would be wasteful.
 *
 * Returns the full `{models, page, total, totalPages}` payload so the
 * caller can render pagination controls.
 */
export function useModelsPaged(opts: { page?: number; pageSize?: number; q?: string }) {
  return useQuery({
    queryKey: ['models', 'page', opts.page ?? 1, opts.pageSize ?? 0, opts.q ?? ''] as const,
    queryFn: ({ signal }) => api.modelsPaged(opts, signal),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: Boolean(opts.pageSize && opts.pageSize >= 1),
    retry: false,
  });
}

export function useHardwarePurchase(id: string | null | undefined, version?: number, hash?: string, modelEpoch?: number) {
  return useQuery({
    queryKey: id ? [...queryKeys.hardwarePurchase(id), version ?? null, hash ?? null, modelEpoch ?? null] : ['hardware-purchase', 'idle'],
    queryFn: ({ signal }) => api.hardwarePurchase(id!, signal), enabled: Boolean(id),
    staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: 'always', refetchOnReconnect: 'always',
    refetchInterval: 15000, retry: false,
  });
}

export function useBundle(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.bundle(id) : ['bundle', 'idle'],
    queryFn: ({ signal }) => api.bundle(id!, signal),
    enabled: Boolean(id),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useScenePipelineReport(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: id ? queryKeys.scenePipelineReport(id) : ['scene-pipeline-report', 'idle'],
    queryFn: () => api.scenePipelineReport(id!),
    enabled: Boolean(id) && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useAnalysis(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.analysis(id) : ['analysis', 'idle'],
    queryFn: () => api.analysis(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * `statureMm` — рост человека для секции "эргономика по росту" (донорская
 * ReachZonesPanel, портированная как надстройка над этим же эндпоинтом, а не
 * как отдельная модель). Опущен — сервер не считает reach-зоны вовсе
 * (см. backend/routers/editing.py, обратная совместимость).
 */
export function useErgonomics(id: string | null | undefined, statureMm?: number) {
  return useQuery({
    queryKey: id ? queryKeys.ergonomics(id, statureMm) : ['ergonomics', 'idle'],
    queryFn: () => api.ergonomics(id!, statureMm),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useProductionSpec(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.productionSpec(id) : ['production-spec', 'idle'],
    queryFn: () => api.productionSpec(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useConstraints(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.constraints(id) : ['constraints', 'idle'],
    queryFn: () => api.constraints(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useDimensionChain(
  id: string | null | undefined,
  axis: 'x' | 'z',
  moduleIds: string[],
) {
  return useQuery({
    queryKey: id ? queryKeys.dimensionChain(id, axis, moduleIds) : ['dimension-chain', 'idle'],
    queryFn: () => api.dimensionChain(id!, axis, moduleIds),
    enabled: Boolean(id && moduleIds.length >= 2),
    staleTime: 10 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useGeometryDiff(
  id: string | null | undefined,
  againstModelId: string | null | undefined,
) {
  return useQuery({
    queryKey: id && againstModelId ? queryKeys.geometryDiff(id, againstModelId) : ['geometry-diff', 'idle'],
    queryFn: () => api.geometryDiff(id!, againstModelId!),
    enabled: Boolean(id && againstModelId && id !== againstModelId),
    staleTime: 10 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useValidation(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.validation(id) : ['validation', 'idle'],
    queryFn: ({ signal }) => api.validation(id!, signal),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useProductionReadiness(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.productionReadiness(id) : ['production-readiness', 'idle'],
    queryFn: () => api.productionReadiness(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** Ordinary Viewer read reuses the bounded canonical resolution owner. */
export function useEstimate(id:string|null|undefined) {
  return useQuery({queryKey:queryKeys.viewerSmetaStatus(id??''),
    queryFn:()=>resolveViewerSmetaBounded(id!),enabled:Boolean(id),staleTime:30_000,
    refetchOnWindowFocus:false,retry:false});
}

export function useCutlist(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.cutlist(id) : ['cutlist', 'idle'],
    queryFn: () => api.cutlist(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useCutlistNesting(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.cutlistNesting(id) : ['cutlist-nesting', 'idle'],
    queryFn: () => api.cutlistNesting(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** (A15) Ведомость остатков поверх раскроя: годные salvage-полосы + Losses. */
export function useOffcutLedger(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.cutlistOffcuts(id) : ['cutlist-offcuts', 'idle'],
    queryFn: () => api.offcutLedger(id!),
    enabled: Boolean(id),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useHistory(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.history(id) : ['history', 'idle'],
    queryFn: () => api.history(id!),
    enabled: Boolean(id),
    staleTime: 20 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useModuleOperations(
  id: string | null | undefined,
  moduleId: string | null | undefined,
  limit = 25,
) {
  return useQuery({
    queryKey: id && moduleId ? queryKeys.moduleOperations(id, moduleId) : ['module-operations', 'idle'],
    queryFn: () => api.moduleOperations(id!, moduleId!, limit),
    enabled: Boolean(id && moduleId),
    staleTime: 10 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useMaterialLibrary() {
  return useQuery({ queryKey: queryKeys.materials, queryFn: api.materials });
}

/** Границы количества наполнения. Таблица меняется с релизом, не с моделью. */
export function useFillCountRanges() {
  return useQuery({
    queryKey: ['fill-count-ranges'],
    queryFn: api.fillCountRanges,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function useCatalogModuleRelations(catalogId: string | null | undefined) {
  return useQuery({
    queryKey: ['catalog-module-relations', catalogId ?? 'idle'],
    queryFn: () => api.catalogModuleRelations(catalogId!),
    enabled: Boolean(catalogId),
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useVariationMatrix(catalogId: string | null | undefined) {
  return useQuery({
    queryKey: ['catalog-variation-matrix', catalogId ?? 'idle'],
    queryFn: () => api.variationMatrix(catalogId!),
    enabled: Boolean(catalogId),
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useBundleGraphs(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.bundleGraphs(id) : ['bundle-graphs', 'idle'],
    queryFn: () => api.bundleGraphs(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useModuleGraph(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.moduleGraph(id) : ['module-graph', 'idle'],
    queryFn: () => api.moduleGraph(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useAutoDimensions(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.autoDimensions(id) : ['auto-dimensions', 'idle'],
    queryFn: () => api.autoDimensions(id!),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useHardwareCatalog() {
  return useQuery({
    queryKey: queryKeys.hardwareCatalog,
    queryFn: api.hardwareCatalog,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useModuleFill(id: string | null | undefined, moduleId: string | null | undefined) {
  return useQuery({
    queryKey: id && moduleId ? queryKeys.moduleFill(id, moduleId) : ['module-fill', 'idle'],
    queryFn: () => api.moduleFill(id!, moduleId!),
    enabled: Boolean(id && moduleId),
    staleTime: 15 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Real facade leaf widths for a door layout BEFORE it is applied.
 *
 * Backed by `POST .../fill/facades/preview-widths`, which runs the exact
 * same `facades` fill command Apply sends, against a throwaway bundle copy,
 * and reads back what it actually built. There is only one width formula —
 * see backend/module_routes_api.py::preview_facade_leaf_widths_route and
 * backend/tests/test_facade_leaf_width_preview.py.
 *
 * `columns <= 0` disables the query: there is nothing to preview before a
 * door count is chosen.
 */
export function useFacadeLeafWidthsPreview(
  id: string | null | undefined,
  moduleId: string | null | undefined,
  payload: Omit<import('./types').FacadeLeafWidthsPreviewPayload, 'expectedBundleVersion'>,
  expectedBundleVersion: number,
) {
  return useQuery({
    queryKey: id && moduleId
      ? queryKeys.facadeLeafWidthsPreview(id, moduleId, payload)
      : ['facade-leaf-widths-preview', 'idle'],
    queryFn: ({ signal }) =>
      api.previewFacadeLeafWidths(id!, moduleId!, { ...payload, expectedBundleVersion }, signal),
    enabled: Boolean(id && moduleId && payload.columns > 0),
    staleTime: 5 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useModuleAtelier(id: string | null | undefined, moduleId: string | null | undefined) {
  return useQuery({
    queryKey: id && moduleId ? queryKeys.moduleAtelier(id, moduleId) : ['module-atelier', 'idle'],
    queryFn: ({ signal }) => api.moduleAtelier(id!, moduleId!, signal),
    enabled: Boolean(id && moduleId),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useFillSchemes() {
  return useQuery({
    queryKey: ['fill-schemes'],
    queryFn: () => api.fillSchemes(),
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useModuleLibrary(
  preview: 'material' | 'line' = 'material',
  params: { q?: string; category?: string; offset?: number; limit?: number } = {},
) {
  const q = params.q?.trim() ?? '';
  const category = params.category?.trim() ?? '';
  const offset = params.offset ?? 0;
  const limit = params.limit ?? 60;
  return useQuery({
    queryKey: queryKeys.moduleLibrary(preview, q, category, offset, limit),
    queryFn: ({ signal }) => api.moduleLibrary({ preview, q, category, offset, limit }, signal),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useRevisionComparison(id: string, before: string, after: string, historyStamp: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.revisionComparison(id, before, after, historyStamp),
    queryFn: async ({ signal }) => {
      const result = await api.compareHistory(id, before, after, signal);
      if (result?.schema !== 'holzcom.revision_comparison.v1' || result.modelId !== id
        || result.before?.id !== before || result.after?.id !== after
        || !/^[a-f0-9]{64}$/.test(result.before?.contentSha256 ?? '') || !/^[a-f0-9]{64}$/.test(result.after?.contentSha256 ?? '')
        || !Array.isArray(result.entities) || !Array.isArray(result.projectFields) || !result.geometry?.summary || !result.summary) {
        throw new Error('REVISION_COMPARISON_RESPONSE_INVALID');
      }
      return result;
    },
    enabled, staleTime: 0, retry: false,
  });
}
